import { MAX_VIDEO_DIMENSION } from './constants';
import { readContainerMetadata } from './containerMetadata';
import { normalizeVideoExtension } from './filename';
import { sniffVideoContainer } from './magicBytes';
import { detectLoudness } from './silenceDetection';

import type { ContainerMetadata } from './containerMetadata';
import type { AudioLoudness } from './silenceDetection';
import type { MediaValidationError, MediaValidationResult } from './types';

export interface ValidateStoryVideoOptions {
  /** Hard size ceiling in bytes (flag-driven; see STORY_VIDEO_SIZE_GB). */
  maxSizeBytes: number;
  /** Injectable readers (defaults to the real ones); used to unit-test logic. */
  sniff?: (file: File) => Promise<'mp4' | 'quicktime' | null>;
  readMetadata?: (file: File) => Promise<ContainerMetadata | null>;
  /**
   * Enable the audio requirement — reject a missing OR silent/muted audio track
   * (LaunchDarkly-gated via enableStoryVideoSilenceCheck).
   */
  requireAudio?: boolean;
  detectLoudness?: (file: File) => Promise<AudioLoudness>;
}

const fail = (error: MediaValidationError): MediaValidationResult => ({
  ok: false,
  error,
});

const formatGb = (bytes: number): string => {
  const gb = bytes / (1024 * 1024 * 1024);
  return `${gb % 1 === 0 ? gb : gb.toFixed(1)}GB`;
};

/**
 * Validate a single story-video file before it is uploaded to S3.
 *
 * Tiers run cheapest-first and short-circuit on the first failure:
 *   0. size cap
 *   0. extension allowlist
 *   0. magic-byte container sniff (fail-closed on unreadable)
 *   1. container metadata: 4K dimension cap (fail-closed)
 *   2. audio requirement — reject missing or silent audio (flag-gated; the
 *      track check is codec-agnostic, silence detection is fail-open)
 */
export async function validateStoryVideo(
  file: File,
  opts: ValidateStoryVideoOptions,
): Promise<MediaValidationResult> {
  const sniff = opts.sniff ?? sniffVideoContainer;
  const readMetadata = opts.readMetadata ?? readContainerMetadata;
  const detectLoudnessFn = opts.detectLoudness ?? detectLoudness;

  // Tier 0a — size.
  if (file.size > opts.maxSizeBytes) {
    return fail({
      reason: 'file-too-large',
      message: `This video is over the ${formatGb(opts.maxSizeBytes)} limit. Please upload a smaller file.`,
      detail: `size=${file.size} max=${opts.maxSizeBytes}`,
    });
  }

  // Tier 0b — extension allowlist.
  const extension = normalizeVideoExtension(file.name);
  if (!extension) {
    return fail({
      reason: 'unsupported-type',
      message: 'Only MP4 and MOV video files are supported.',
      detail: `name=${file.name}`,
    });
  }

  // Tier 0c — magic-byte container sniff (fail-closed: an unreadable file is
  // rejected rather than passed through).
  let container: 'mp4' | 'quicktime' | null;
  try {
    container = await sniff(file);
  } catch (e) {
    return fail({
      reason: 'unreadable',
      message: "We couldn't read this file. Please try a different video.",
      detail: e instanceof Error ? e.message : String(e),
    });
  }
  if (!container) {
    return fail({
      reason: 'not-a-video',
      message: 'This file is not a valid MP4 or MOV video.',
      detail: `name=${file.name}`,
    });
  }

  // Tier 1 — container metadata (audio track + dimensions). Fail-closed: a file
  // whose moov we can't parse is rejected.
  const metadata = await readMetadata(file);
  if (!metadata) {
    return fail({
      reason: 'unreadable',
      message:
        "We couldn't read this video's format. Please try a different file.",
      detail: 'moov-parse-failed',
    });
  }
  const longEdge = Math.max(metadata.width, metadata.height);
  const shortEdge = Math.min(metadata.width, metadata.height);
  if (
    longEdge > MAX_VIDEO_DIMENSION.width ||
    shortEdge > MAX_VIDEO_DIMENSION.height
  ) {
    return fail({
      reason: 'resolution-too-high',
      message: `This video is larger than 4K (${metadata.width}×${metadata.height}). Please upload 4K or smaller.`,
      detail: `${metadata.width}x${metadata.height}`,
    });
  }

  // Tier 2 — audio requirement (opt-in via flag). Rejects both a missing audio
  // track and a present-but-silent/muted one, so the "stories need audible
  // audio to transcribe" guard ships as a single toggle. Track presence is
  // codec-agnostic; silence detection is fail-open (undecodable audio → allow).
  let audioChecked = false;
  if (opts.requireAudio) {
    if (!metadata.hasAudioTrack) {
      return fail({
        reason: 'no-audio-track',
        message:
          'This video has no audio track. Stories need audio to generate a transcript.',
        detail: `duration=${metadata.durationSec}`,
      });
    }
    const loudness = await detectLoudnessFn(file);
    audioChecked = loudness !== 'undetermined';
    if (loudness === 'silent') {
      return fail({
        reason: 'audio-silent',
        message:
          "This video's audio appears to be silent or muted. Stories need audible audio to generate a transcript.",
        detail: 'rms-below-threshold',
      });
    }
  }

  return {
    ok: true,
    value: {
      sizeBytes: file.size,
      extension,
      container,
      width: metadata.width,
      height: metadata.height,
      durationSec: metadata.durationSec,
      hasAudioTrack: metadata.hasAudioTrack,
      audioChecked,
    },
  };
}
